/**
 * JOURNAL PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlusIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Screen, Section, Button, Select, Input } from '../design/primitives';
import { SPACING, COLORS, TYPOGRAPHY, RADIUS } from '../design/tokens';
import JournalList from '../components/JournalList';
import JournalForm from '../components/forms/JournalForm';
import { apiClient } from '../api/apiConfig';

interface FilterOptions {
  startDate: string;
  endDate: string;
  businessId: string;
  projectId: string;
  categoryId: string;
  entryType: string;
}

const JournalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isJournalFormOpen, setIsJournalFormOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [entryIdToOpen, setEntryIdToOpen] = useState<number | null>(null);
  const hasProcessedParams = useRef(false);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: '',
    endDate: '',
    businessId: '',
    projectId: '',
    categoryId: '',
    entryType: '',
  });

  const [businesses, setBusinesses] = useState<Array<{ id: number; name: string }>>([]);
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);

  const handleJournalEntryCreated = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1);
  }, []);

  const handleEntryOpened = useCallback(() => {
    setEntryIdToOpen(null);
  }, []);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [businessesRes, projectsRes, categoriesRes] = await Promise.all([
          apiClient.get('/api/businesses/'),
          apiClient.get('/api/projects/'),
          apiClient.get('/api/categories/'),
        ]);
        setBusinesses(businessesRes.data);
        setProjects(projectsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      businessId: '',
      projectId: '',
      categoryId: '',
      entryType: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  useEffect(() => {
    if (hasProcessedParams.current) return;

    const entryIdParam = searchParams.get('entryId');
    if (entryIdParam) {
      const entryId = parseInt(entryIdParam, 10);
      if (!isNaN(entryId)) {
        setEntryIdToOpen(entryId);
        hasProcessedParams.current = true;
        searchParams.delete('entryId');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  return (
    <Screen>
      <Section
        title="Journal"
        action={
          <div style={{ display: 'flex', gap: SPACING[12] }}>
            <Button
              variant={showFilters || hasActiveFilters ? 'primary' : 'secondary'}
              onClick={() => setShowFilters(!showFilters)}
              icon={<FunnelIcon style={{ width: '16px', height: '16px' }} />}
            >
              Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v).length})`}
            </Button>
            <Button
              variant="primary"
              onClick={() => setIsJournalFormOpen(true)}
              icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
            >
              New Entry
            </Button>
          </div>
        }
      >
        {showFilters && (
          <div style={{
            backgroundColor: COLORS.bg.panel,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            padding: SPACING[16],
            marginBottom: SPACING[16],
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: SPACING[16],
            }}>
              <h3 style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '600',
                color: COLORS.text.primary,
                margin: 0,
              }}>
                Filter Journal Entries
              </h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: COLORS.accent.default,
                    cursor: 'pointer',
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    fontWeight: '500',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: SPACING[16],
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  End Date
                </label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Entry Type
                </label>
                <Select
                  value={filters.entryType}
                  onChange={(e) => handleFilterChange('entryType', e.target.value)}
                  options={[
                    { value: '', label: 'All Types' },
                    { value: 'reference', label: 'Reference' },
                    { value: 'thought', label: 'Thought' },
                    { value: 'knowledge', label: 'Knowledge' },
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Business
                </label>
                <Select
                  value={filters.businessId}
                  onChange={(e) => handleFilterChange('businessId', e.target.value)}
                  options={[
                    { value: '', label: 'All Businesses' },
                    ...businesses.map(b => ({ value: b.id.toString(), label: b.name })),
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Project
                </label>
                <Select
                  value={filters.projectId}
                  onChange={(e) => handleFilterChange('projectId', e.target.value)}
                  options={[
                    { value: '', label: 'All Projects' },
                    ...projects.map(p => ({ value: p.id.toString(), label: p.name })),
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}>
                  Category
                </label>
                <Select
                  value={filters.categoryId}
                  onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                  options={[
                    { value: '', label: 'All Categories' },
                    ...categories.map(c => ({ value: c.id.toString(), label: c.name })),
                  ]}
                />
              </div>
            </div>
          </div>
        )}

        <JournalList key={refreshKey} entryIdToOpen={entryIdToOpen} onEntryOpened={handleEntryOpened} filters={filters} />
      </Section>

      <JournalForm
        isOpen={isJournalFormOpen}
        onClose={() => setIsJournalFormOpen(false)}
        onJournalEntryCreated={handleJournalEntryCreated}
      />
    </Screen>
  );
};

export default JournalPage;
